import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, AlertCircle, AlertTriangle } from 'lucide-react';
import type { FaceRecognitionReasonCodeBreakdown } from '../../api/face-recognition.schemas';

interface ReasonCodeBreakdownChartProps {
  data?: FaceRecognitionReasonCodeBreakdown;
  loading?: boolean;
  error?: Error;
  onRetry?: () => void;
}

/**
 * ReasonCodeBreakdownChart Component
 * 
 * Displays a chart showing Face Recognition reason code breakdown.
 * Shows top N reason codes in a horizontal bar chart format.
 * Only displayed when breakdown data is available from BFF.
 * 
 * Requirements: 2.8, 6.2
 */
export function ReasonCodeBreakdownChart({ 
  data, 
  loading = false, 
  error,
  onRetry
}: ReasonCodeBreakdownChartProps) {
  
  // Loading state
  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Top Reason Codes</CardTitle>
          </div>
          <CardDescription>Most common rejection and error reasons</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 p-6 pt-0 space-y-3">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    <Skeleton className="w-3 h-3 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="h-5 w-16 mb-1" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                  <div className="text-right">
                    <Skeleton className="h-4 w-12 mb-1" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <Skeleton className="w-full h-2 rounded-full" />
              </div>
            ))}
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <Skeleton className="h-4 w-32 mb-2" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Skeleton className="h-3 w-20 mb-1" />
                <Skeleton className="h-5 w-12" />
              </div>
              <div>
                <Skeleton className="h-3 w-16 mb-1" />
                <Skeleton className="h-4 w-8" />
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-muted">
              <Skeleton className="h-3 w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Top Reason Codes</CardTitle>
          </div>
          <CardDescription>Failed to load reason code data</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Unable to display chart</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
            {onRetry && (
              <Button onClick={onRetry} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!data || data.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Top Reason Codes</CardTitle>
          </div>
          <CardDescription>Most common rejection and error reasons</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <AlertTriangle className="h-6 w-6 mx-auto mb-2" />
            <p>No reason code data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sort data by count (highest to lowest) and calculate total
  const sortedData = [...data].sort((a, b) => b.count - a.count);
  const total = data.reduce((sum, item) => sum + item.count, 0);
  const maxValue = Math.max(...data.map(item => item.count));

  // Color palette for reason codes
  const colors = [
    'bg-red-500',
    'bg-orange-500',
    'bg-yellow-500',
    'bg-blue-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-green-500',
    'bg-gray-500'
  ];

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Top Reason Codes</CardTitle>
        </div>
        <CardDescription>
          {total.toLocaleString()} total occurrences across {data.length} reason codes
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 space-y-3 overflow-y-auto">
          {sortedData.map((item, index) => {
            const percentage = total > 0 ? (item.count / total) * 100 : 0;
            const barWidth = maxValue > 0 ? (item.count / maxValue) * 100 : 0;
            const colorClass = colors[index % colors.length];
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${colorClass}`} />
                    <div className="min-w-0 flex-1">
                      <span className="font-medium font-mono text-xs bg-muted px-2 py-1 rounded">
                        {item.code}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        {percentage.toFixed(1)}% of total
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <span className="font-semibold">{item.count.toLocaleString()}</span>
                    <p className="text-xs text-muted-foreground">occurrences</p>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`${colorClass} h-2 rounded-full transition-all duration-500 ease-out`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Summary */}
        <div className="mt-4 p-3 bg-muted/50 rounded-lg flex-shrink-0">
          <h4 className="font-medium text-sm mb-2">Reason Code Analysis</h4>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-muted-foreground">Most Common:</span>
              <div className="font-medium font-mono text-xs bg-muted px-2 py-1 rounded mt-1">
                {sortedData[0]?.code || 'N/A'}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Unique Codes:</span>
              <div className="font-semibold mt-1">
                {data.length}
              </div>
            </div>
          </div>
          {sortedData.length > 0 && (
            <div className="mt-2 pt-2 border-t border-muted">
              <div className="text-xs text-muted-foreground">
                Top reason accounts for {((sortedData[0].count / total) * 100).toFixed(1)}% of all occurrences
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}